import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Globe } from 'lucide-react';
import { COMPANY } from '../constants';
import { useLanguage } from '../i18n/LanguageContext';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, navLinks, homePath, altPath, lang } = useLanguage();

  return (
    <footer
      className="bg-gray-900 pt-12 sm:pt-16 pb-6"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <Link to={homePath} className="inline-block mb-4 bg-white p-2 rounded">
              <img
                src={COMPANY.logo}
                alt="Reco Pneus"
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-md mb-6">
              {t.footer.desc}
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={COMPANY.phoneLink}
                className="inline-flex items-center gap-2 text-white hover:text-[#41DB23] transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4 text-[#41DB23]" />
                <span className="font-bold">{COMPANY.phone}</span>
              </a>
              <a
                href={COMPANY.emailLink}
                className="inline-flex items-center gap-2 text-white hover:text-[#41DB23] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4 text-[#41DB23]" />
                <span>{COMPANY.email}</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">
              {t.footer.navTitle}
            </h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-[#41DB23] text-sm transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">
              {t.footer.infoTitle}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <Clock className="w-4 h-4 text-[#41DB23] flex-shrink-0 mt-0.5" />
                <span>{COMPANY.hours}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <MapPin className="w-4 h-4 text-[#41DB23] flex-shrink-0 mt-0.5" />
                <span>{COMPANY.coverageArea}</span>
              </li>
            </ul>

            {/* Language Switcher in Footer */}
            <div className="mt-6">
              <Link
                to={altPath}
                className="inline-flex items-center gap-1.5 text-gray-400 hover:text-[#41DB23] text-sm transition-colors"
                data-testid="lang-switcher-footer"
              >
                <Globe className="w-4 h-4" />
                {lang === 'fr' ? 'English version' : 'Version française'}
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs">
              &copy; {currentYear} {COMPANY.name}. {t.footer.copyright}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
              <Link
                to="/mentions-legales"
                className="text-gray-500 hover:text-[#41DB23] transition-colors"
                data-testid="footer-mentions"
              >
                {t.footer.mentions}
              </Link>
              <span className="text-gray-700">|</span>
              <Link
                to="/politique-confidentialite"
                className="text-gray-500 hover:text-[#41DB23] transition-colors"
                data-testid="footer-privacy"
              >
                {t.footer.privacy}
              </Link>
              <span className="text-gray-700">|</span>
              <Link
                to="/cookies"
                className="text-gray-500 hover:text-[#41DB23] transition-colors"
                data-testid="footer-cookies"
              >
                {t.footer.cookiesLink}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
